import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup, Card, Row, Col
} from 'react-bootstrap';
import { 
  FaEdit, FaTrash, FaPlus, FaSearch,
  FaMapMarkerAlt, FaGlobeAmericas
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const Zones = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    zoneName: '',
    centerLat: '',
    centerLon: '',
    radiusKm: 50
  });
  const [editMode, setEditMode] = useState(false);
  const autocompleteInput = useRef(null);

  useEffect(() => {
    fetchZones();
    loadGoogleMapsScript();
  }, [searchTerm, currentPage]);

  const loadGoogleMapsScript = () => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  };

  const initAutocomplete = () => {
    if (window.google && autocompleteInput.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        autocompleteInput.current,
        { types: ['(regions)'] } // Search for cities/regions
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) return;

        setFormData(prev => ({
          ...prev,
          zoneName: place.name,
          centerLat: place.geometry.location.lat(),
          centerLon: place.geometry.location.lng()
        }));
      });
    }
  };

  const fetchZones = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/zones`, {
        headers: { 'x-auth-token': token },
        params: { 
          page: currentPage,
          search: searchTerm 
        }
      });
      
      setZones(response.data.zones);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error fetching zones:', err);
      setError(err.response?.data?.error || 'Failed to load zones');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      if (editMode) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/zones/${formData._id}`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Zone updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/zones`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Zone added successfully');
      }
      
      setShowForm(false);
      fetchZones();
    } catch (err) {
      console.error('Error saving zone:', err);
      setError(err.response?.data?.error || 'Failed to save zone');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/zones/${zoneToDelete._id}`,
        { headers: { 'x-auth-token': token } }
      );
      
      setShowDeleteModal(false);
      fetchZones();
      toast.success('Zone deleted successfully');
    } catch (err) {
      console.error('Error deleting zone:', err);
      setError(err.response?.data?.error || 'Failed to delete zone');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (zone) => {
    setFormData(zone);
    setEditMode(true);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      zoneName: '',
      centerLat: '',
      centerLon: '',
      radiusKm: 50
    });
    setEditMode(false);
  };

  return (
    <div className="zones-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Service Zones</h2>
        <Button 
          variant="primary" 
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <FaPlus className="me-2" />
          Add New Zone
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {showForm ? (
        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Zone Name</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaMapMarkerAlt />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="zoneName"
                        value={formData.zoneName}
                        onChange={handleInputChange}
                        placeholder="Search zone (e.g., Brisbane)"
                        required
                        ref={autocompleteInput}
                        onFocus={initAutocomplete}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Radius (km)</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaGlobeAmericas />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="radiusKm"
                        value={formData.radiusKm}
                        onChange={handleInputChange}
                        placeholder="Service radius"
                        min="1"
                        step="1"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Latitude</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaGlobeAmericas />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="centerLat"
                        value={formData.centerLat}
                        onChange={handleInputChange}
                        placeholder="Latitude"
                        step="any"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Longitude</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaGlobeAmericas />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="centerLon"
                        value={formData.centerLon}
                        onChange={handleInputChange}
                        placeholder="Longitude"
                        step="any"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : editMode ? (
                    'Update Zone'
                  ) : (
                    'Add Zone'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search zones"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Zone Name</th>
                    <th>Coordinates</th>
                    <th>Service Radius</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {zones.length > 0 ? (
                    zones.map((zone) => (
                      <tr key={zone._id}>
                        <td>{zone.zoneName}</td>
                        <td>
                          {zone.centerLat}, {zone.centerLon}
                        </td>
                        <td>{zone.radiusKm} km</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEdit(zone)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setZoneToDelete(zone);
                                setShowDeleteModal(true);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No service zones found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <li 
                          key={page} 
                          className={`page-item ${currentPage === page ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the zone <strong>{zoneToDelete?.zoneName}</strong>?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Zones;